import { watch } from "vue";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import {
    useAuthenticationService,
    AuthStatus,
    handleAuthenticated,
    handleUnauthenticated,
} from "@/composables/authentication";

// General
import Help from "@/views/Help.vue";
import Login from "@/views/Login.vue";
import Home from "@/views/Home.vue";

// BizDevOps
import Biz from "@/views/Biz.vue";
import Dev from "@/views/Dev.vue";
import Ops from "@/views/Ops.vue";

// Utility pages
import RedirectByNamePage from "@/views/RedirectByNamePage.vue";
import RedirectByIdPage from "@/views/RedirectByIdPage.vue";

// BizDevOps subitems
import Application from "@/views/Application.vue";
import Dashboard from "@/views/Dashboard.vue";
import Domain from "@/views/Domain.vue";
import DomainPage from "@/views/DomainPage.vue";
import Process from "@/views/Process.vue";
import Product from "@/views/Product.vue";
import Service from "@/views/Service.vue";
import UserManagement from "@/views/UserManagement.vue";

// Instances
import OrgInst from "@/views/OrgInst.vue";
import ProcessPage from "@/views/ProcessPage.vue";
import ProductPage from "@/views/ProductPage.vue";
import RolePage from "@/views/RolePage.vue";
import TaskPage from "@/views/TaskPage.vue";
import DocumentPage from "@/views/DocumentPage.vue";
import ProcessDefinitionPage from "@/views/ProcessDefinitionPage.vue";
import StekPage from "@/views/StekPage.vue";

import NotFoundComponent from "@/views/NotFound.vue";

// Create
import CreateStek from "@/views/CreateStek.vue";

// Live
import Live from "@/live/views/Live.vue";

import Profile from "@/views/Profile.vue";
import type * as Layouts from "@/layouts";
import mixpanel from "mixpanel-browser";
import GatewayPage from "@/views/GatewayPage.vue";
import FlowPage from "@/views/FlowPage.vue";
import EventPage from "@/views/EventPage.vue";

declare module "vue-router" {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresOrg?: boolean;
        requiresStek?: boolean;
        breadcrumbs?: string[] | "auto";
        layout?: keyof typeof Layouts;
        mode?: "design" | "live";
    }
}

const routes: Array<RouteRecordRaw> = [
    {
        path: "/home",
        name: "home",
        component: Home,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/login",
        name: "login",
        component: Login,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/biz",
        name: "biz",
        component: Biz,
    },
    {
        path: "/dev",
        name: "dev",
        component: Dev,
    },
    {
        path: "/ops",
        name: "ops",
        component: Ops,
    },
    {
        path: "/domain",
        name: "domain",
        component: Domain,
    },
    {
        path: "/product",
        name: "product",
        component: Product,
    },
    {
        path: "/process",
        name: "process",
        component: Process,
    },
    {
        path: "/application",
        name: "application",
        component: Application,
    },
    {
        path: "/service",
        name: "service",
        component: Service,
    },
    {
        path: "/help",
        name: "help",
        component: Help,
    },
    {
        path: "/profile",
        name: "profile",
        component: Profile,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/:orgName?",
        name: "organizationPage",
        component: OrgInst,
        meta: {
            requiresAuth: true,
            requiresOrg: true,
            requiresStek: true,
            layout: "ApplicationLayout",
        },
        children: [
            {
                path: "stek/create",
                name: "createStek",
                component: CreateStek,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                },
            },
            {
                path: "user",
                name: "userManagement",
                component: UserManagement,
                meta: {
                    requiresStek: false,
                    requiresAuth: true,
                    requiresOrg: true,
                },
            },
            {
                path: "steks",
                component: undefined,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: true,
                },
                children: [
                    {
                        path: ":stekName",
                        component: undefined,
                        children: [
                            {
                                path: "live",
                                component: Live,
                                meta: {
                                    mode: "live",
                                },
                                children: [
                                    {
                                        path: "",
                                        name: "dashboard",
                                        component: Dashboard,
                                    },
                                ],
                            },
                            {
                                path: "",
                                alias: "biz/domain",
                                name: "stekPage",
                                component: StekPage,
                            },
                            {
                                path: "domain/:domainId",
                                name: "domainPage",
                                component: DomainPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId/document/:artefactId?",
                                name: "domainDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "product/:productId",
                                name: "productPage",
                                component: ProductPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "role/:roleId",
                                name: "rolePage",
                                component: RolePage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "product/:productId/document/:artefactId?",
                                name: "productDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId",
                                name: "processPage",
                                component: ProcessPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/process-definition/definition/:version?",
                                name: "processDefinitionPage",
                                component: ProcessDefinitionPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/document/:artefactId?",
                                name: "processDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId/task/:taskId",
                                name: "taskPage",
                                component: RedirectByIdPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/task/:taskId/document/:artefactId?",
                                name: "taskDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "task/:taskId",
                                name: "taskPage",
                                component: TaskPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "gateway/:gatewayId",
                                name: "gatewayPage",
                                component: GatewayPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "event/:eventId",
                                name: "eventPage",
                                component: EventPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "flow/:flowId",
                                name: "flowPage",
                                component: FlowPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "document/:artefactId/:version?",
                                name: "documentPage",
                                component: DocumentPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "biz/domain/:domainName",
                                name: "domainDashboard",
                                component: RedirectByNamePage,
                                children: [
                                    {
                                        path: "",
                                        name: "domainOverview",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "roles",
                                        name: "roles",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "entities",
                                        name: "entities",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "files/:filePath?",
                                        name: "domainFiles",
                                        component: RedirectByNamePage,
                                    },
                                ],
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/:filePath?",
                                name: "productInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/:filePath?",
                                name: "processInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/tasks/:taskName/:filePath?",
                                name: "taskInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                name: "not-found",
                                path: "not-found",
                                component: NotFoundComponent,
                            },
                            {
                                path: ":pathMatch(.*)*",
                                component: NotFoundComponent,
                            },
                        ],
                    },
                    {
                        path: ":pathMatch(.*)*",
                        component: NotFoundComponent,
                    },
                ],
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        component: NotFoundComponent,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        window.scrollTo(0, 0);
    },
});

export let routerIsReady = false;
// Make sure we can do something right after the router is ready.
router.isReady().then(() => {
    const authenticationService = useAuthenticationService();
    routerIsReady = true;

    // Sometimes the authentication library is initialized before the router is,
    // in those cases we still need to redirect to the right pages.
    if (authenticationService.authStatus.value === AuthStatus.SIGNED_IN) {
        handleAuthenticated();
    } else if (
        authenticationService.authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        handleUnauthenticated();
    }
});

router.beforeEach(async (guard) => {
    mixpanel.track_pageview({
        "Page Name": guard.name,
        "Page URL": guard.path,
        ...guard.params,
    });

    const authenticationService = useAuthenticationService();
    // There is a brief period in which the authentication library is still
    // initializing. In this period the authentication status is unknown.
    // As soon as the authentication status is known it will be updated in
    // the store, so we utilize a short-lived watcher so that we can wait
    // for the authentication status to be updated in the router guard.
    if (authenticationService.authStatus.value === AuthStatus.UNKNOWN) {
        let unwatch: () => void;
        const initialized = new Promise<void>((resolve) => {
            unwatch = watch(
                () => authenticationService.authStatus.value,
                (authStatus: AuthStatus) => {
                    if (authStatus !== AuthStatus.UNKNOWN) {
                        resolve();
                    }
                },
                {
                    // Using immediate to make sure that we don't accidentally miss
                    // the authstatus update in case it was updated before the
                    // watcher was created.
                    immediate: true,
                }
            );
        });

        // Using 'then' so it doesn't seem to checkers that unwatch can be called
        // while uninitialized.
        await initialized.then(() => {
            unwatch();
        });
    }
});

export default router;
